/**
 * Sign up, sign in, sessions, and account recovery.
 *
 * Methods that speak the domain rather than the transport — a screen calls
 * `signIn`, never `apiPost("/auth/login", …)`. That is what keeps the wire
 * changeable without touching a page.
 *
 * # Two things a screen must not do
 *
 * 1. **Do not infer whether an address has an account.** `signUp`,
 *    `resendVerification` and `requestPasswordReset` all resolve identically
 *    whether or not it does. That is deliberate (`src/http/auth/`), and a
 *    "this email is already registered" message built on top of it would be
 *    both wrong and the exact leak the server refuses.
 * 2. **Do not treat validation here as a check.** Anything this file rejects is
 *    rejected again server-side; the point of doing it here is a faster error,
 *    not a safer one.
 */

import {
  apiGet,
  apiPost,
  HttpError,
  type Accepted,
  type Acknowledged,
  type EmailRequest,
  type LoginRequest,
  type SessionView,
  type SignupRequest,
  type TokenRequest,
  type MeView,
  apiSend,
  apiPutBytes,
} from "./http.js";

/**
 * Create an account.
 *
 * Resolves the same way whether or not the address was already registered — see
 * the note above. What differs is the message that lands in the inbox.
 */
export async function signUp(request: SignupRequest): Promise<Accepted> {
  return apiPost<Accepted>("/auth/signup", request);
}

/** Prove an address, with the token from a verification link. */
export async function verifyEmail(token: string): Promise<Acknowledged> {
  const request: TokenRequest = { token };
  return apiPost<Acknowledged>("/auth/verify", request);
}

/** Ask for another verification link. */
export async function resendVerification(email: string): Promise<Accepted> {
  const request: EmailRequest = { email };
  return apiPost<Accepted>("/auth/resend-verification", request);
}

/**
 * Sign in. The session cookie is set by the server and never touched here.
 *
 * Throws [`HttpError`] with code `invalid_credentials` for a wrong email or
 * password — one code for both, because the server will not say which — or
 * `account_locked`, which is only ever returned when the password was right.
 */
export async function signIn(request: LoginRequest): Promise<SessionView> {
  return apiPost<SessionView>("/auth/login", request);
}

/** Sign out. Succeeds even with no session, so it is safe to retry. */
export async function signOut(): Promise<Acknowledged> {
  return apiPost<Acknowledged>("/auth/logout", {});
}

/**
 * Who is signed in, or `null` when nobody is.
 *
 * Not signed in is a normal state for this app rather than an error — most
 * people who touch it are guests (ADR 0007) — so it resolves to `null` instead
 * of throwing. Anything else still throws.
 */
export async function currentSession(): Promise<SessionView | null> {
  try {
    return await apiGet<SessionView>("/auth/session");
  } catch (error) {
    if (error instanceof HttpError && error.code === "unauthenticated") {
      return null;
    }
    throw error;
  }
}

/**
 * Ask for a passwordless sign-in link.
 *
 * Answers `check_your_email` whether or not the address has an account, and
 * creates one if it does not — so a caller must never branch on the reply to
 * say "welcome back" or "we've made you an account". It does not know, and
 * that is the point (ADR 0010).
 */
export async function requestSignInLink(email: string): Promise<Accepted> {
  const request: EmailRequest = { email };
  return apiPost<Accepted>("/auth/link", request);
}

/**
 * Spend a sign-in link and open a session.
 *
 * **Call this once.** The token is single-use, so React StrictMode's double
 * effect will spend it and then fail on the second call — the caller has to
 * guard, the way `/verify` does.
 */
export async function consumeSignInLink(token: string): Promise<SessionView> {
  const request: TokenRequest = { token };
  return apiPost<SessionView>("/auth/link/consume", request);
}

/** Ask for a password-reset link. */
export async function requestPasswordReset(email: string): Promise<Accepted> {
  const request: EmailRequest = { email };
  return apiPost<Accepted>("/auth/password/forgot", request);
}

/**
 * Set a new password with the token from a reset link.
 *
 * Signs out every device, including this one — the caller has to route to the
 * sign-in screen afterwards rather than assuming the session survived.
 */
export async function resetPassword(
  token: string,
  password: string,
): Promise<Acknowledged> {
  const request: TokenRequest = { token, password };
  return apiPost<Acknowledged>("/auth/password/reset", request);
}

// ---------------------------------------------------------------------------
// The account's own settings
//
// Each of these acts on the caller's profile, resolved from the session — no
// call names a profile, so a client cannot ask about somebody else's.
// ---------------------------------------------------------------------------

/** Everything the settings screen renders. */
export async function currentProfile(): Promise<MeView> {
  return apiGet<MeView>("/me");
}

/**
 * What a profile can remember — phase 24.
 *
 * Optional everywhere, because a profile that has never expressed a preference
 * has an empty object and every reader has to cope with that anyway.
 */
export interface Preferences {
  /**
   * Whether the *How to play* walkthrough has been seen or skipped.
   *
   * Dismissed once, gone for good — still reachable from the menu, so it is
   * opt-in afterwards rather than lost.
   */
  seen_how_to_play?: boolean;
}

/**
 * What this profile has asked to be remembered.
 *
 * ⚠️ **The client owns the shape.** The server stores an opaque object and caps
 * its size; nothing in `src/` reads a key out of it, and the moment something
 * does, that key should be a real column instead (`migrations/0034`).
 *
 * Per profile rather than per device, which is what ADR 0027 as amended made
 * possible: every player holds an approved account now, so *"I have seen this"*
 * follows somebody to a new phone instead of starting over.
 */
export async function preferences(): Promise<Preferences> {
  const view = await apiGet<{ preferences: Preferences }>("/me/preferences");
  return view.preferences;
}

/**
 * Remember these keys, keeping everything else.
 *
 * **A merge, not a replace**, all the way down to the SQL: two devices signed in
 * at once both hold a preferences object, and a replacing write means whichever
 * saves second silently discards what the other set.
 */
export async function remember(set: Preferences): Promise<Preferences> {
  const view = await apiSend<{ preferences: Preferences }>("PATCH", "/me/preferences", {
    set,
  });
  return view.preferences;
}

/** Rename yourself. Answers with the whole view, so a caller never re-fetches. */
export async function updateDisplayName(displayName: string): Promise<MeView> {
  return apiSend<MeView>("PATCH", "/me/profile", { display_name: displayName });
}

/**
 * Set a password, or change one.
 *
 * `current` is omitted by an account that has none — one that only ever signs in
 * by emailed link. The server decides whether it was required; a client that
 * guessed would be enforcing an authorisation rule from the outside.
 */
export async function setPassword(next: string, current?: string): Promise<Acknowledged> {
  return apiPost<Acknowledged>("/me/password", {
    new: next,
    ...(current === undefined ? {} : { current }),
  });
}

/** Upload an avatar. The bytes are the body; the server re-encodes them. */
export async function uploadAvatar(file: Blob): Promise<MeView> {
  return apiPutBytes<MeView>("/me/avatar", file);
}

/** Go back to the generated avatar. */
export async function clearAvatar(): Promise<MeView> {
  return apiSend<MeView>("DELETE", "/me/avatar");
}
